// How messages show their time (Settings → Appearance, #52). One set of rules so every client shows the same words;
// scripts/timestamp-cases.mjs checks them against the issue's examples. Clients draw these with tabular figures, so a
// count that ticks every second ("91s ago") keeps its width.

/** default: the clock time (3:04 PM); relative: 12 minutes ago; exact: 6d 12:33 ago. */
export type TimestampFormat = "default" | "relative" | "exact";
export const TIMESTAMP_FORMATS: TimestampFormat[] = ["default", "relative", "exact"];

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
/** Months and years in relative times are 30 and 360 days, so "2 months" starts at 60 days and "2 years" at 24 months. */
const MONTH = 30 * DAY;
const YEAR = 12 * MONTH;

/**
 * Relative units, largest first: each shows from `from` on. A unit shows until there are 2 of the next one, except
 * hours, which go on to 72 (25 hours, 49 hours, then 3 days).
 */
const UNITS = [
  { from: 2 * YEAR, ms: YEAR, word: "years", short: "y" },
  { from: 2 * MONTH, ms: MONTH, word: "months", short: "mo" },
  { from: 3 * DAY, ms: DAY, word: "days", short: "d" },
  { from: 2 * HOUR, ms: HOUR, word: "hours", short: "h" },
  { from: 2 * MINUTE, ms: MINUTE, word: "minutes", short: "m" },
  // "91s ago", not "91 seconds ago": it changes every second, and the short word keeps it calm.
  { from: 10 * SECOND, ms: SECOND, word: "s", short: "s" },
] as const;

function relativeUnit(ageMs: number) {
  const unit = UNITS.find((u) => ageMs >= u.from);
  return unit ? { n: Math.floor(ageMs / unit.ms), unit } : null;
}

/** "just now", "63s ago", "92 minutes ago", "49 hours ago", "3 days ago", "2 months ago". */
export function relativeAge(ageMs: number): string {
  const r = relativeUnit(ageMs);
  if (!r) return "just now";
  return r.unit.ms === SECOND ? `${r.n}s ago` : `${r.n} ${r.unit.word} ago`;
}

/** The narrow form beside grouped messages: "now", "63s", "92m", "49h", "3d", "2mo", "2y". */
export function relativeAgeShort(ageMs: number): string {
  const r = relativeUnit(ageMs);
  return r ? `${r.n}${r.unit.short}` : "now";
}

function exactParts(ageMs: number) {
  const minutes = Math.floor(Math.max(0, ageMs) / MINUTE);
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor(minutes / 60) % 24;
  return { days, clock: `${hours}:${String(minutes % 60).padStart(2, "0")}` };
}

/** Days, then hours:minutes: "6d 12:33 ago"; under a day, "0:12 ago". */
export function exactAge(ageMs: number): string {
  const { days, clock } = exactParts(ageMs);
  return `${days ? `${days}d ` : ""}${clock} ago`;
}

/** The narrow form: "6d" from a day on, else "0:12". */
export function exactAgeShort(ageMs: number): string {
  const { days, clock } = exactParts(ageMs);
  return days ? `${days}d` : clock;
}
